/** Remove trailing slashes (always returns string) */
function trimTrailingSlashes(value?: string | null): string {
  if (!value) return "";
  let end = value.length;
  while (end > 0 && value.codePointAt(end - 1) === 47) end--;
  return value.slice(0, end);
}

/** Remove leading slashes (always returns string) */
function trimLeadingSlashes(value?: string | null): string {
  if (!value) return "";
  let start = 0;
  while (start < value.length && value.codePointAt(start) === 47) start++;
  return value.slice(start);
}

/** Safe decode (always returns string) */
function safeDecode(value?: string | null): string {
  if (!value) return "";
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function normalizeSlashes(path: string): string {
  return path.replaceAll(/\/+/g, "/");
}

export function extractRelativePath2(url: string): string {
  const API_MEDIA_PREFIX = "/api/media/";
  const bucketBase = trimTrailingSlashes(process.env.REACT_APP_BUCKET_URL);

  try {
    const u = new URL(url);

    // 1. Local API
    const apiIndex = u.pathname.indexOf(API_MEDIA_PREFIX);
    if (apiIndex !== -1) {
      const relative = trimLeadingSlashes(u.pathname.slice(apiIndex + API_MEDIA_PREFIX.length));
      return normalizeSlashes(relative);
    }

    // 2. GCS (public or nested paths)
    if (u.hostname.endsWith("storage.googleapis.com")) {
      const parts = u.pathname.split("/").filter(Boolean); // keep all segments
      return normalizeSlashes(safeDecode(parts.join("/")));
    }

    // 3. Bucket base URL
    if (bucketBase && url.startsWith(bucketBase)) {
      const relative = trimLeadingSlashes(url.slice(bucketBase.length));
      return normalizeSlashes(safeDecode(relative));
    }

    // 4. Fallback
    return normalizeSlashes(safeDecode(trimLeadingSlashes(u.pathname)));
  } catch {
    return normalizeSlashes(url ?? "");
  }
}

export function extractRelativePath(url: string): string {
  const API_MEDIA_PREFIX = "/api/media/";
  const bucketBase = trimTrailingSlashes(process.env.REACT_APP_BUCKET_URL ?? "");

  try {
    // Support relative URLs by providing a dummy base (use HTTPS instead of HTTP)
    const u = new URL(url, "https://placeholder");

    // 1. Local API
    const apiIndex = u.pathname.indexOf(API_MEDIA_PREFIX);
    if (apiIndex !== -1) {
      return normalizeSlashes(
        trimLeadingSlashes(u.pathname.slice(apiIndex + API_MEDIA_PREFIX.length))
      );
    }

    // 2. GCS (public or nested paths)
    if (u.hostname.endsWith("storage.googleapis.com")) {
      const parts = u.pathname.split("/").filter(Boolean);

      // Remove bucket name
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const [_bucket, ...objectParts] = parts;

      return normalizeSlashes(safeDecode(objectParts.join("/")));
    }

    // 3. Bucket base (normalize both sides)
    const normalizedUrl = trimTrailingSlashes(url);
    if (bucketBase && normalizedUrl.startsWith(bucketBase)) {
      const relative = normalizedUrl.slice(bucketBase.length);
      return normalizeSlashes(safeDecode(trimLeadingSlashes(relative)));
    }

    // 4. Fallback: pathname only
    return normalizeSlashes(safeDecode(trimLeadingSlashes(u.pathname)));
  } catch {
    // If URL parsing fails completely
    return normalizeSlashes(url ?? "");
  }
}
